import { Skeleton } from '@tempo-io/sui-skeleton';
import { useTranslation } from 'react-i18next';
import { EmptyFieldValue } from '@/features/items/components/item-field-value/EmptyFieldValue';
import { Divider, Row, Section } from './financialPanelLayout';
import { SavableUnitField } from './SavableUnitField';
import type { FinancialSaveState, ItemFinancialFigures } from './types';
import { MAX_MONETARY_AMOUNT } from './types';
import { useLiveDraft } from './useLiveDraft';

interface BudgetSectionProps {
  amount: number | null;
  figures?: ItemFinancialFigures;
  effectiveBudget: number | null;
  symbol: string;
  money: (value: number | null | undefined) => string;
  percent: (value: number | null | undefined) => string;
  isReadOnly: boolean;
  isLoading: boolean;
  isLowestLevel?: boolean;
  saveState: FinancialSaveState;
  onSave: (value: number | null) => void;
  onAmountChange?: (value: number | null) => void;
  onCapexPercentChange?: (value: number | null) => void;
  capexSaveState?: FinancialSaveState;
  showHeaderValue?: boolean;
  defaultOpen?: boolean;
}

export const BudgetSection = ({
  amount,
  figures,
  effectiveBudget,
  symbol,
  money,
  percent,
  isReadOnly,
  isLoading,
  isLowestLevel = false,
  saveState,
  onSave,
  onAmountChange,
  onCapexPercentChange,
  capexSaveState = { status: 'idle' },
  showHeaderValue = false,
  defaultOpen = true,
}: BudgetSectionProps) => {
  const { t } = useTranslation();
  const below = figures?.descendantsBudgetAmount ?? null;
  const capexPercent = figures?.capexPercent ?? null;
  const canEditCapex = figures?.hierarchyRootSource === 'SELF';
  const isCapexInherited = figures?.hierarchyRootSource === 'PARENT';
  const [liveCapexPercent, setLiveCapexPercent] = useLiveDraft(capexPercent);

  const capexAmount =
    effectiveBudget != null && liveCapexPercent != null
      ? (effectiveBudget * liveCapexPercent) / 100
      : null;
  const opexAmount =
    effectiveBudget != null && capexAmount != null ? effectiveBudget - capexAmount : null;

  const budgetLabel = t(($) => $.financials.panel.budgetedCostAtThisLevel);
  const capexLabel = t(($) => $.financials.panel.capexPercent);

  const capexSublabel = () => {
    if (isCapexInherited) return t(($) => $.financials.panel.capexInheritedFromParent);
    if (showHeaderValue) return t(($) => $.financials.panel.capexPercentSublabel);
    return undefined;
  };

  const capexValue = () => {
    if (isLoading) {
      return <Skeleton className="h-9 w-36" data-testid="financial-capex-skeleton" />;
    }
    if (isReadOnly || !onCapexPercentChange || !canEditCapex) {
      return capexPercent != null ? percent(capexPercent) : <EmptyFieldValue />;
    }
    return (
      <SavableUnitField
        saveState={capexSaveState}
        value={capexPercent}
        unit="%"
        unitSide="right"
        ariaLabel={capexLabel}
        decimals={2}
        min={0}
        max={100}
        maxDecimals={2}
        onCommit={onCapexPercentChange}
        onChange={setLiveCapexPercent}
        testId="financial-capex-input"
      />
    );
  };

  const budgetValue = () => {
    if (isLoading) {
      return <Skeleton className="h-9 w-36" data-testid="financial-budget-skeleton" />;
    }
    if (isReadOnly) {
      return amount === null ? <EmptyFieldValue /> : <span>{money(amount)}</span>;
    }
    return (
      <SavableUnitField
        saveState={saveState}
        value={amount}
        unit={symbol}
        unitSide="left"
        ariaLabel={budgetLabel}
        decimals={2}
        max={MAX_MONETARY_AMOUNT}
        maxDecimals={2}
        onCommit={onSave}
        onChange={onAmountChange}
        testId="financial-budget-input"
      />
    );
  };

  return (
    <Section
      title={t(($) => $.financials.panel.budgetSectionTitle)}
      subtitle={
        isLowestLevel
          ? t(($) => $.financials.panel.budgetSectionSubtitleLeaf)
          : t(($) => $.financials.panel.budgetSectionSubtitleEffective)
      }
      headerValue={showHeaderValue ? money(effectiveBudget) : undefined}
      defaultOpen={defaultOpen}
    >
      <Row label={budgetLabel} right={budgetValue()} />

      {figures && (
        <>
          {below != null && !isLowestLevel && (
            <Row
              label={t(($) => $.financials.panel.budgetBelowThisLevel)}
              sublabel={t(($) => $.financials.panel.budgetBelowSublabel)}
              right={money(below)}
            />
          )}
          <Row
            label={t(($) => $.financials.panel.budgetTotal)}
            sublabel={isLowestLevel ? undefined : t(($) => $.financials.panel.budgetTotalSublabel)}
            right={money(effectiveBudget)}
            variant="total"
          />
          <Divider />
          <Row label={capexLabel} sublabel={capexSublabel()} right={capexValue()} />
          <Row
            label={t(($) => $.financials.panel.capexAmount)}
            sublabel={t(($) => $.financials.panel.capexAmountSublabel)}
            right={money(capexAmount)}
          />
          <Row
            label={t(($) => $.financials.panel.opexAmount)}
            sublabel={t(($) => $.financials.panel.opexAmountSublabel)}
            right={money(opexAmount)}
          />
          {figures.plannedLaborHours != null && (
            <>
              <Divider />
              <Row
                label={t(($) => $.financials.panel.plannedLaborHours)}
                right={t(($) => $.financials.panel.plannedLaborHoursValue, {
                  hours: figures.plannedLaborHours.toLocaleString('en-US'),
                })}
              />
            </>
          )}
          {figures.durationLabel && (
            <Row label={t(($) => $.financials.panel.duration)} right={figures.durationLabel} />
          )}
        </>
      )}
    </Section>
  );
};
